<template>
  <div class="breadcrumbs-main electron-no-drag">
    <div
      class="breadcrumbs-wrap usn"
      :class="{ 'bg-background-strongest': viewEditMode }"
    >
      <div
        v-if="displayFirstLevel"
        class="breadcrumb-item"
        :class="[{ 'disabled-item': viewEditMode }, { 'disable-hover': disableFirstLevelHover }]"
        @click="firstLevelClickHandler"
      >
        <em
          v-if="displayFirstLevelIcon"
          :style="getCustomIconStyle(iconPath(firstLevelIcon))"
          class="custom-icons m-1 bg-icon-default"
          :class="viewEditMode ? 'bg-inactive-icon' : 'disable-item'"
        />
        <span
          v-if="!(isView && isMobile)"
          class="breadcrumb-item-text transparent-border"
        >
          {{ firstLevelTitle }}
        </span>
      </div>

      <!-- display project as first level -->
      <a
        v-else
        class="breadcrumb-item project-name no-underline"
        :href="spaceLink"
        @click="goToSpace"
        @dragstart.prevent
      >
        <ProjectIcon
          :project-icon="getProjectIcon.src || ''"
          :project-color="getProjectIcon.color || ''"
          :type="getProjectIcon.type || iconEnum"
        />
        <span
          v-if="displaySpaceTitle"
          ref="projectTitle"
          v-tooltip="{
            content: projectTitle,
            onShow: () => !!projectTitleTitleTooltip,
            placement: 'bottom',
          }"
          class="breadcrumb-item-text transparent-border"
          :class="{ 'safari-hidden': isSafari }"
        >
          {{ projectTitle }}
        </span>
        <div
          v-else-if="displaySpaceTitleEditable"
          class="breadcrumb-item editable-item"
          :class="{
            'edit-active': hasFocusProjectTitle,
            'blank-field': !projectNameInput,
            'focused-input': hasFocusProjectTitle,
          }"
        >
          <AutoSizeInput
            ref="headerNameProjectTitleInput"
            v-tooltip="{
              content: translate('rename'),
              placement: 'bottom',
              onShow: () => !readOnly || !isGuestReader,
            }"
            :value="projectNameInput"
            :border-less="true"
            class="breadcrumb-item-text"
            :placeholder="placeholder"
            :disabled="isGuestRole || isGuestReader"
            @input="($event) => (projectNameInput = $event.target.value)"
            @keydown="checkKeyProjectTitle"
            @blur="handleProjectTitleInputBlur"
            @focus="handleProjectTitleInput"
            @click.stop.prevent
          />
        </div>
      </a>
      <span
        v-if="displaySeparator"
        class="breadcrumb-separator"
        :class="{ 'disabled-item': newBoardView || viewEditMode }"
      >
        /
      </span>

      <BreadcrumbPageParents v-if="type === 'page'" />

      <a
        v-if="displayItemTitle && !isBoardDeleted"
        :href="resourceLink"
        class="breadcrumb-item no-underline"
        :class="{
          'editable-item px-1': editableResourceName,
          'edit-active': editableItem,
          'blank-field': !inputValue,
          'focused-input': hasFocus,
          'view-edit-mode': viewEditMode,
          'disable-hover': isCreateSpace,
        }"
        @click="openPageOrBoard"
        @dragstart.prevent
      >
        <ResourceIcon
          v-if="isPage"
          :src="page?.icon?.src || 'page'"
          :color="page?.icon?.color"
          :type="page?.icon?.type"
        />
        <em
          v-if="sprintsOpened || sprintOpened"
          class="custom-icons bg-icon-defaul"
          :style="getCustomIconStyle(iconPath('sprint-completed'))"
        />
        <span
          v-if="!editableResourceName"
          ref="itemTitle"
          v-tooltip="{
            content: itemTitle,
            onShow: () => !!itemTitleTitleTooltip,
            placement: 'bottom',
          }"
          class="breadcrumb-item-text transparent-border"
        >
          {{ itemTitle }}
        </span>
        <AutoSizeInput
          v-if="editableResourceName"
          ref="headerNameInput"
          v-tooltip="{
            content: translate('rename'),
            onShow: () => !readOnly && !isGuestReader,
            placement: 'bottom',
          }"
          :value="inputValue"
          :border-less="true"
          class="breadcrumb-item-text"
          :class="{ 'placeholder:text-red-700': isViewOpen }"
          :placeholder="placeholder"
          :disabled="readOnly || isGuestReader"
          @input="($event) => (inputValue = $event.target.value)"
          @keydown="checkKey"
          @keyup.enter="openFilterMenu"
          @blur="handleInputBlur"
          @focus="handleInputFocus"
        />
      </a>
      <span
        v-if="(cardOpened || isEpic || sprintOpened) && !isMobile"
        class="breadcrumb-separator"
      >
        /
      </span>
      <v-popover
        v-if="(cardOpened || isEpic) && !isMobile"
        interactive
        tag="div"
        content-tag="div"
        :role="'poper'"
        :trigger="'click'"
        :on-show="listDropdownEnabled"
      >
        <template #default="{ state }">
          <div
            class="breadcrumb-item"
            :class="{ 'breadcrumb-list-active': state.isVisible }"
          >
            <em
              class="custom-icon flex w-4 h-4"
              :style="{
                ...getCustomIconStyle(iconPath(selectedStatus.icon)),
                backgroundColor: selectedStatus.color,
              }"
            />
            <span class="breadcrumb-item-text transparent-border"> {{ listTitle }} </span>
          </div>
        </template>
        <template #content="{ state, hide }">
          <ListCardStatusPicker
            v-if="state.isVisible"
            class="list-dropdown"
            :selected-status="selectedStatus"
            :custom-statuses-list="formatedLists"
            :allow-color-change="false"
            :show-dropdown-label="false"
            @esc="hide"
            @click="listClickHandler($event, hide)"
          />
        </template>
      </v-popover>
      <div
        v-if="sprintOpened"
        class="breadcrumb-item"
      >
        <ResourceIcon
          v-if="sprintOpened"
          :src="sprintStateIconName"
          :color="sprintStateIconColor"
          :type="'icon'"
        />
        <div
          v-if="sprintOpened"
          class="breadcrumb-item-text"
          @click="generateLink(type)"
        >
          {{ board.title }}
        </div>
      </div>
      <span
        v-if="cardOpened || isEpic"
        class="breadcrumb-separator"
      >
        /
      </span>
      <div
        v-if="cardOpened"
        v-tooltip="{
          onShow: () => !card?.archived,
          content: translate('viewCardInBoard'),
          placement: 'bottom',
        }"
        class="breadcrumb-item card-breadcrumb"
        @click="cardIdClickHandler"
      >
        {{ cardIdWithPrefix }}
      </div>
      <div
        v-if="isEpic"
        class="breadcrumb-item"
        @click="clickOnEpic"
      >
        <ResourceIcon
          v-if="isEpic"
          :src="card.icon?.src || $constants.epicDefaultIcon"
          :color="card.icon?.color"
          :type="card.icon?.type"
        />
        <div
          v-if="isEpic"
          class="breadcrumb-item-text font-medium"
        >
          {{ card.title }}
        </div>
      </div>
      <Tabs
        v-if="isEpic"
        :items="epicTabItems"
        class="ml-1"
        @click="epicTabClick"
      >
        <ChildCardsPreviewButton
          v-if="isEpic && childCardsCount"
          class="mr-0 ml-1 pointer-events-none border-0 bg-transparent in-tab-progress"
          :cards-count="childCardsCount"
          :done-cards-count="doneCardsCount"
          :started-cards-count="startedCardsCount"
          :has-expand-button="false"
        />
      </Tabs>
    </div>
    <div class="breadcrumb-buttons">
      <div
        v-if="showCopyLinkButton"
        v-tooltip="{
          content: urlTooltipContent,
          placement: 'bottom',
        }"
        class="copy-link-button pt"
        @click="generateLink(type, resourceId)"
      >
        <em
          :style="getCustomIconStyle(iconPath('link-st'))"
          class="custom-icons copy-header-icon"
        />
      </div>
      <div
        v-if="(newBoardView || isNewView || isNewPage) && !isMobile"
        class="copy-link-button"
        :class="{ 'disabled-item': newBoardView || isNewView || isNewPage }"
      >
        <em
          :style="getCustomIconStyle(iconPath('link-st'))"
          class="custom-icons"
        />
      </div>
      <v-popover
        v-if="gitHubIntegrated && cardOpened && !isMobile"
        tag="div"
        content-tag="div"
        placement="bottom"
        class="copy-link-button pt"
        @click="generateGitBranchName(resourceId)"
      >
        <em
          :style="getCustomIconStyle(iconPath('git-pull-request'))"
          class="custom-icons copy-header-icon"
        />
        <template #content="{ state }">
          <TooltipTemplate
            v-if="state.isVisible"
            :tooltip-text="translate('copyGitBranchNameToClipboard')"
            :tooltip-meta="{
              icon: {
                key: '.',
                modifiers: ['ctrl', 'shift'],
              },
            }"
          />
        </template>
      </v-popover>
    </div>
  </div>
</template>

<script>
import AutoSizeInput from '@/utilities/AutoSizeInput';
import getDashedTitle from '@/utilities/getDashedTitle';
import customIconsMixin from '@/mixins/customIconsMixin';
import resourceActionsMixin from '@/mixins/resourceActionsMixin';
import cardStatuses from '@/mixins/cardStatuses';
import BreadcrumbPageParents from '@/components/widgets/BreadcrumbPageParents';
import breadcrumbTooltipMixin from '@/mixins/breadcrumbTooltipMixin';
import BookmarkMixin from '@/mixins/BookmarkMixin';
import EventBus from '@/utilities/eventBus';
import ProjectIcon from '@/components/elements/common/ProjectIcon';
import ResourceIcon from '@/components/elements/common/ResourceIcon';
import { ResourceType } from '@/types/resources';
import getMemberLabel from '@/utilities/getMemberLabel';
import moveCardMixin from '@/mixins/moveCardMixin';
import ListCardStatusPicker from '@/components/widgets/ListCardStatusPicker';
import { computed } from 'vue';
import { useStore } from 'vuex';
import useBoardEmbed from '@/boards/composables/useBoardEmbed';
import useIsBrowser from '@/utilities/composables/useIsBrowser.ts';
import useSprintStateIcon from '@/utilities/composables/useSprintStateIcon';
import TooltipTemplate from '@/components/elements/TooltipTemplate.vue';
import Tabs from '@/components/widgets/Tabs';
import useTabs from '@/utilities/composables/useTabs';
import useCheckSprintState from '@/utilities/composables/useCheckSprintState';
import { getSprintKey } from '@/utilities/sprintKeys';
import childCardCounterMixin from '@/mixins/childCardCounterMixin';
import ChildCardsPreviewButton from '@/components/widgets/ChildCardsPreviewButton.vue';
import { getCardStatus } from '@/utilities/defaultCardStatuses';
import useCurrentRole from '@/utilities/composables/useCurrentRole';
import { cmdClickOpenInNewTab } from '@/utilities/openInNewTab';
import useEpicFeature from '@/utilities/composables/useEpicFeature';

export default {
  name: 'Breadcrumbs',

  components: {
    AutoSizeInput,
    BreadcrumbPageParents,
    ProjectIcon,
    ResourceIcon,
    ListCardStatusPicker,
    Tabs,
    ChildCardsPreviewButton,
    TooltipTemplate,
  },

  mixins: [
    customIconsMixin,
    resourceActionsMixin,
    cardStatuses,
    breadcrumbTooltipMixin,
    BookmarkMixin,
    moveCardMixin,
    childCardCounterMixin,
  ],

  props: {
    type: {
      type: String,
      required: true,
    },

    resourceId: {
      type: String,
      default: '',
    },

    // eslint-disable-next-line vue/no-unused-properties
    isTemplate: {
      // isTemplate is required for child card counter mixin
      type: Boolean,
      default: false,
    },
  },

  emits: ['saveViewTitle'],

  setup(props) {
    const store = useStore();

    const { embeddedBoardAttributes, embed, inEpicBoard } = useBoardEmbed();
    const { isSafari } = useIsBrowser();
    const { isGuestReader } = useCurrentRole();
    const readOnly = computed(() => embeddedBoardAttributes.value?.readOnly);
    const { sprintStateIcon } = useSprintStateIcon();
    const { getEpicTabs } = useTabs();

    const currentCard = computed(() => store.getters.getCardById(props.resourceId) || {});

    const { cancelledOrCompletedSprint } = useCheckSprintState(currentCard);
    const { isRoadmapWithoutFeatureFlag } = useEpicFeature();

    return {
      embeddedBoardAttributes,
      embed,
      inEpicBoard,
      readOnly,
      isSafari,
      sprintStateIcon,
      getEpicTabs,
      cancelledOrCompletedSprint,
      isGuestReader,
      isRoadmapWithoutFeatureFlag,
    };
  },

  data() {
    return {
      showLinkIcon: true,
      editableItem: false,
      inputValue: '',
      hasFocus: false,
      hasFocusProjectTitle: false,
      projectNameInput: '',
    };
  },

  computed: {
    epicSelectedTabId() {
      return this.isEpic ? this.$store.getters.getEpicSelectedTabId(this.resourceId) : '';
    },
    epicTabItems() {
      return this.getEpicTabs(this.epicSelectedTabId);
    },
    selectedStatus() {
      const listId = this.card.sprint_id
        ? `${this.boardId}-${this.card.list_id}`
        : this.card.list_id;
      const selectedList = this.formatedLists.find((l) => l.list_id === listId) || {};
      return {
        id: selectedList.id || 'backlog',
        color: selectedList.color,
        listId: selectedList.list_id,
        icon: selectedList.icon,
      };
    },

    formatedLists() {
      const resourceBelongsToId = this.card.sprint_id
        ? getSprintKey(this.card.project_id, this.card.sprint_id)
        : this.card.board_id;
      const listIds = this.$store.getters.boardsListsIds[resourceBelongsToId] || [
        this.card?.list_id,
      ];
      if (!listIds) return [];
      return listIds
        .map((listId) => {
          const list = this.$store.getters.lists[listId];
          if (list?.orginalId !== this.$constants.rolledOverList) {
            const isEpic = this.card?.type === ResourceType.Epic;
            return {
              id: list?.behavior,
              label: list?.title,
              color:
                list?.color ||
                (getCardStatus(list?.behavior, isEpic) || this.cardStatuses[this.card?.status])
                  ?.color ||
                this.cardStatuses.backlog.color ||
                '',
              icon:
                (getCardStatus(list?.behavior, isEpic) || this.cardStatuses[this.card?.status])
                  ?.icon ||
                list?.icon ||
                this.cardStatuses.backlog.icon ||
                '',
              list_id: list?.id || this.card?.list_id,
            };
          }
          return null; // Exclude items where orginalId === 'rolledover'
        })
        .filter((item) => item !== null);
    },

    currentProject() {
      return this.$store.getters.getCurrentProject;
    },

    getProjectIcon() {
      if (this.displayProjectPreviewBoards && !this.typeCard) {
        return this.$store.getters.previewProject.icon || { src: '', color: '' };
      }
      return this.resourceProject.icon || { src: '', color: '' };
    },

    projectTitle() {
      if ((this.displayProjectPreviewBoards && !this.typeCard) || this.type === '') {
        return this.$store.getters.previewProject.title || '';
      }
      return this.resourceProject.title || '';
    },

    displayProjectPreviewBoards() {
      return !!this.$store.getters.displayProjectPreviewBoards;
    },

    tabName() {
      return this.$store.getters.getCurrentTabName;
    },

    showTypeIcon() {
      return this.type === 'boards' || this.type === 'pages';
    },

    boardOrPageOpened() {
      return this.type === 'board' || this.type === 'page';
    },

    pageOpened() {
      return this.type === 'page';
    },

    boardOpened() {
      return this.type === 'board';
    },

    spaceOpened() {
      return this.type === 'boards';
    },

    cardOpened() {
      return this.typeCard;
    },

    newBoardView() {
      return this.type === 'newboard';
    },

    sprintsOpened() {
      return this.type === ResourceType.Sprints;
    },

    sprintOpened() {
      return this.type === ResourceType.Sprint;
    },

    boardIcon() {
      return this.type === 'boards' || this.type === 'board' || this.typeCard || this.newBoardView;
    },

    pageIcon() {
      return this.type === 'pages' || this.type === 'page';
    },

    boardTitle() {
      if (this.typeCard || this.isEpic) {
        return this.board?.title || this.card.board_title;
      }

      return this.currentBoard.title || this.board?.title;
    },

    boardId() {
      return this.board?.id;
    },

    page() {
      return this.$store.getters.getPage;
    },

    pageTitle() {
      return this.page.title || this.translate('pageTitlePlaceholder');
    },
    pageId() {
      return this.page.id;
    },

    card() {
      return this.typeCard || this.isEpic ? this.$store.getters.getCardById(this.resourceId) : {};
    },

    cardId() {
      return this.card.id;
    },

    typeCard() {
      return [ResourceType.Card, ResourceType.SprintCard].includes(this.type);
    },

    listTitle() {
      return this.list.title || this.card.list_title;
    },

    cardIdWithPrefix() {
      return this.typeCard ? `C-${this.resourceId}` : '';
    },

    newBoardTitle() {
      return this.$store.getters.getNewBoardTitle;
    },

    viewTitle() {
      return this.$store.getters.getSelectedView?.name || '';
    },

    newBoardObject() {
      return this.$store.getters.getNewBoardObject;
    },

    itemTitle() {
      switch (this.type) {
        case ResourceType.Note:
          return this.$store.getters.getNotes[this.resourceId]?.title || '';
        case 'view':
          return this.viewTitle;
        case 'board':
        case 'card':
        case 'sprintcard':
          return this.boardTitle;
        case 'page':
          return this.pageTitle;
        case 'newpage':
          return '...';
        case 'newboard':
          return this.newBoardObject?.title || '';
        case this.$constants.createSpaceRouteName:
          return this.translate('createSpace');
        case this.$constants.routeNames.sprintsRouteName:
        case this.$constants.routeNames.sprint:
          return this.translate('sprints');
        default:
          return '';
      }
    },
    resourceProject() {
      const typeMapping = {
        board: this.currentBoard,
        page: this.page,
      };
      switch (this.type) {
        case 'board':
        case 'page':
          const item = typeMapping[this.type];
          return (
            this.getProjectById(item.project_id) ||
            this.$store.getters.getTrashProjectById(item.project_id) ||
            this.currentProject
          );
        case 'card':
        case 'sprintcard':
          return (
            this.getProjectById(this.card.project_id || this.currentBoard.project_id) ||
            this.currentProject
          );
        default:
          return this.currentProject;
      }
    },

    teamSlug() {
      return this.$store.getters.getTeamSlug;
    },

    currentProjectPath() {
      return `${this.$cons.projectSlug}-${this.currentProject.id}`;
    },

    lists() {
      if (!this.typeCard) return [];
      // TODO In dropdown for moving card to another list loop directly through boardsListsIds
      const listIds =
        this.$store.getters.boardsListsIds[
          this.card.board_id || getSprintKey(this.card.project_id, this.card.sprint_id)
        ];
      const flatLists = [];
      listIds.forEach((l) => {
        if (l !== this.card.list_id) {
          flatLists.push(this.$store.getters.lists[l]);
        }
      });
      return flatLists;
    },

    listCards() {
      return this.$store.getters.getListCards;
    },

    list() {
      const listId = this.card.sprint_id
        ? `${this.boardId}-${this.card.list_id}`
        : this.card.list_id;
      return this.$store.getters.lists[listId] || {};
    },

    editableResourceName() {
      if (this.type === 'board' && this.currentBoard.archived) {
        return false;
      }
      return ['board', 'view', 'newview'].includes(this.type);
    },

    urlTooltipContent() {
      switch (this.type) {
        case 'card':
        case 'sprintcard':
          return this.translate('copyCardUrl');
        case 'page':
          return this.translate('copyPageUrl');
        case 'board':
          return this.translate('copyBoardUrl');
        case 'boards':
        case 'pages':
          return this.translate('copySpaceUrl');
        default:
          return this.translate('copyUrl');
      }
    },

    currentBoard() {
      if (this.embed) {
        return this.$store.getters.getFlatBoards[this.embeddedBoardAttributes?.boardId] || {};
      }
      return this.$store.getters.currentBoard;
    },

    board() {
      const boardId =
        this.embeddedBoardAttributes?.boardId || this.card.sprint_id
          ? getSprintKey(this.card.project_id, this.card.sprint_id)
          : this.card.board_id ||
            this.getListById(this.card.list_id).board_id ||
            this.currentBoard.id;
      return this.$store.getters.getFlatBoards[boardId] || {};
    },

    placeholder() {
      switch (this.type) {
        case 'newview':
        case 'view':
          return this.translate('untitledView');
        case 'newboard':
          return this.translate('enterBoardTitle');
        case 'card':
        case 'sprintcard':
        case 'page':
        case 'board':
        case 'boards':
        case 'pages':
        default:
          return '';
      }
    },

    isNewBoard() {
      return this.$route.name === 'newboard';
    },

    teamProjects() {
      return this.$store.getters.getTeamProjects;
    },

    cards() {
      return this.$store.getters.getCards || {};
    },

    showCopyLinkButton() {
      return (
        (this.cardOpened ||
          this.isEpic ||
          this.boardOrPageOpened ||
          this.showTypeIcon ||
          this.isViewOpen ||
          this.isMemeberWork ||
          this.isManageSpaces ||
          this.isCreateSpace ||
          this.isEpicsBoard ||
          this.isSprint ||
          this.isSprints) &&
        !this.isMobile &&
        !this.isNewView &&
        !this.isNewPage &&
        !this.isBoardDeleted &&
        !this.isRoadmapWithoutFeatureFlag
      );
    },

    displaySeparator() {
      return (
        this.boardOpened ||
        this.newBoardView ||
        (this.cardOpened && !this.isMobile) ||
        (this.pageOpened && !this.isMobile) ||
        (this.isViewOpen && !this.isViews && !this.isMobile) ||
        this.isCreateSpace ||
        this.sprintsOpened ||
        this.sprintOpened ||
        (this.isNote && this.resourceId)
      );
    },

    displaySpaceTitle() {
      return (
        this.boardOpened ||
        this.cardOpened ||
        this.newBoardView ||
        (this.pageOpened && !this.isMobile) ||
        this.sprintsOpened ||
        this.sprintOpened
      );
    },

    displaySpaceTitleEditable() {
      return (this.pageOpened && !this.isMobile) || this.spaceOpened;
    },

    displayItemTitle() {
      return (
        this.boardOrPageOpened ||
        (this.cardOpened && !this.isMobile) ||
        this.newBoardView ||
        (this.isViewOpen && !this.isViews) ||
        this.isCreateSpace ||
        this.sprintsOpened ||
        this.sprintOpened ||
        (this.isNote && this.resourceId)
      );
    },

    viewEditMode() {
      return this.$store.getters.getViewEditMode;
    },

    isView() {
      return this.$route.name === this.$constants.routeNames.view;
    },

    isNewView() {
      return this.$route.name === this.$constants.routeNames.newView;
    },

    isViews() {
      return this.$route.name === this.$constants.routeNames.views;
    },

    isManageSpaces() {
      return this.$route.name === this.$constants.manageSpacesRouteName;
    },

    isEpicsBoard() {
      return this.$route.name === this.$constants.epicsBoardRoute.name;
    },

    isCreateSpace() {
      return this.$route.name === this.$constants.createSpaceRouteName;
    },

    isEpic() {
      return this.type === ResourceType.Epic;
    },

    isNote() {
      return this.type === ResourceType.Note;
    },

    isMyWork() {
      return [this.$constants.myWorkRouteName, this.$constants.memberRouteName].includes(
        this.$route.name
      );
    },

    isAgents() {
      return this.$route.name === this.$constants.routeNames.agents;
    },

    isAgent() {
      return (
        this.$route.name === this.$constants.routeNames.newAgent ||
        this.$route.name === 'updateAgent'
      );
    },

    firstLevelIcon() {
      if (this.isEpicsBoard || this.isEpic) return 'map';
      if (this.isAgents || this.isAgent) return 'cpu';
      if (this.isNote && this.resourceId) return 'note';
      return this.isViewOpen ? 'view' : 'my-work';
    },

    isMemeberWork() {
      return this.$constants.memberRouteName.includes(this.$route.name);
    },

    displayFirstLevelIcon() {
      return (
        this.isViewOpen ||
        this.isMyWork ||
        this.isAgents ||
        this.isAgent ||
        this.isEpicsBoard ||
        this.isEpic ||
        this.isNote
      );
    },

    displayFirstLevel() {
      return (
        (this.isViewOpen ||
          this.isEpic ||
          this.isMyWork ||
          this.isAgents ||
          this.isAgent ||
          this.isManageSpaces ||
          this.isCreateSpace ||
          this.isEpicsBoard ||
          this.isNote) &&
        !this.cardOpened
      );
    },

    disableFirstLevelHover() {
      return (
        this.isViews ||
        this.isManageSpaces ||
        this.isMyWork ||
        this.isAgents ||
        this.isAgent ||
        (this.isEpicsBoard && !this.isEpic)
      );
    },

    firstLevelTitle() {
      if (this.isEpic) return this.translate('roadmap');

      switch (this.$route.name) {
        case this.$constants.routeNames.agents:
          return this.translate('Agents');
        case this.$constants.routeNames.newAgent:
          return this.translate('New agent');
        case this.$constants.routeNames.updateAgent:
          return this.translate('Update agent');
        case this.$constants.routeNames.view:
        case this.$constants.routeNames.newView:
        case this.$constants.routeNames.views:
          return this.translate('views');
        case this.$constants.myWorkRouteName:
          return this.translate('my-work');
        case this.$constants.createSpaceRouteName:
        case this.$constants.manageSpacesRouteName:
          return this.translate('manageSpaces');
        case this.$constants.memberRouteName:
          return this.memberNameToDisplay;
        case this.$constants.epicsBoardRoute.name:
          return this.translate('roadmap');
        case this.$constants.routeNames.notes:
        case this.$constants.routeNames.note:
          return this.translate('notes');
        default:
          return '';
      }
    },

    memberNameToDisplay() {
      const userId = this.$store.getters.getMyWorkUserId;
      const member = this.$store.getters.getMemberById(userId);
      const nameToBeUsed = getMemberLabel(member, true);
      const letterSAtEnd = nameToBeUsed.toLowerCase().endsWith('s');
      const handleSAtEnd = letterSAtEnd ? "'" : "'s";
      return `${nameToBeUsed}${handleSAtEnd} ${this.translate('work')}`;
    },

    isPage() {
      return this.type === ResourceType.Page;
    },

    inboxCard() {
      return this.inboxResource?.resource_type === ResourceType.Card;
    },

    inboxPage() {
      return this.inboxResource?.resource_type === ResourceType.Page;
    },

    inboxResource() {
      return this.$store.getters.getCurrentInboxResource;
    },

    isNewPage() {
      return this.$route.name === this.$constants.newPage;
    },

    getTeamId() {
      return this.$store.getters.getTeamID;
    },

    gitHubIntegrated() {
      return this.$store.getters.getIntegrationInstallations('github').length > 0;
    },

    sprintStateIconName() {
      return this.sprintStateIcon(this.board?.state)?.icon;
    },

    sprintStateIconColor() {
      return this.sprintStateIcon(this.board?.state)?.color;
    },

    isBoardDeleted() {
      return this.$store.getters.getFlatBoards[`${this.resourceId}_notFound`];
    },

    routeTabName() {
      switch (this.tabName) {
        case 'boards':
        case 'cards':
        case 'lists':
        case 'pages':
          return 'boards';
        default:
          return '';
      }
    },

    spaceLink() {
      return `/${this.teamSlug}/${this.currentProjectPath}/${this.routeTabName}`;
    },

    pageLink() {
      return `/${this.teamSlug}/page-${this.pageId}-${getDashedTitle(this.pageTitle)}`;
    },

    resourceLink() {
      if (this.type === ResourceType.Page) {
        return this.pageLink;
      }

      if (this.typeCard) {
        const resourceName = this.card.sprint_id ? ResourceType.Sprint : ResourceType.Board;
        const itemId = this.card.sprint_id
          ? getSprintKey(this.card.project_id, this.card.sprint_id)
          : this.card.board_id;
        return `/${this.teamSlug}/${resourceName}-${itemId}-${getDashedTitle(this.boardTitle)}`;
      }

      if (this.type === ResourceType.Sprint) {
        return `/${this.teamSlug}/${this.currentProjectPath}/${this.$constants.routeNames.sprintsRouteName}`;
      }

      if (this.type === ResourceType.Board) {
        return `/${this.teamSlug}/board-${this.boardId}-${getDashedTitle(this.boardTitle)}`;
      }

      return this.spaceLink;
    },
  },

  watch: {
    type(newValue) {
      if (newValue === 'newview') {
        this.focusInputField();
      }
    },
    itemTitle(newValue) {
      this.inputValue = newValue;
    },
    projectTitle(newValue) {
      this.projectNameInput = newValue;
    },
  },

  mounted() {
    if (this.typeCard) {
      this.fetchDataForCardBreadcrumbs();
    }

    if (this.type === 'newview') {
      this.editableItem = true;
      this.$nextTick(() => {
        this.$refs.headerNameInput.focus();
      });
    }

    if (this.itemTitle !== '') {
      this.inputValue = this.itemTitle;
    }

    if (this.projectTitle !== '') {
      this.projectNameInput = this.projectTitle;
    }
  },

  methods: {
    epicTabClick(tabId) {
      EventBus.$emit('epic:changeTab', {
        newTabId: tabId,
        cardId: this.resourceId,
      });
    },
    clickOnEpic() {
      const cardTitle = getDashedTitle(this.card.title);
      this.$router.push({
        path: `/${this.teamSlug}/${this.$constants.epicRoute.path}-${this.card.id}-${cardTitle}`,
        query: { userNavigated: true, cardIdButton: true },
      });
    },
    // fetches requiered data to display and open board in card breadcrumbs
    fetchDataForCardBreadcrumbs() {
      const { board_id, sprint_id, list_id } = this.card;

      // case when card is not archived (archived cards dont have board_id property)
      if ((board_id || sprint_id) && !this.$isBoardFullyLoaded(this.board, !!sprint_id)) {
        sprint_id ? this.fetchSprint(sprint_id) : this.fetchBoard(board_id);
        return;
      }

      // case when card is archived, use board_id from list to fetch board
      if (!list_id || !board_id) return;
      this.fetchList(list_id).then((boardId) => {
        if (boardId) {
          this.fetchBoard(boardId);
        }
      });
    },
    fetchSprint(sprintId) {
      if (this.$isBoardFullyLoaded(this.board, true)) return;
      this.$store.dispatch('getSprint', { sprintId, projectId: this.card.project_id });
    },
    fetchBoard(boardId) {
      if (this.$isBoardFullyLoaded(this.board)) return;
      this.$store.dispatch('fetchBoard', { boardId });
    },
    fetchList(listId) {
      const list = this.getListById(listId);
      return Object.keys(list).length
        ? Promise.resolve(list.board_id)
        : this.$store
            .dispatch('getList', {
              teamId: this.getTeamId,
              listId,
            })
            .then((resp) => {
              this.$store.dispatch('setBoardListsFlat', [resp.list]);
              return resp.list.board_id;
            });
    },
    getListById(listId) {
      return this.$store.getters.lists[listId] || {};
    },
    listClickHandler(list, hideFn) {
      if (typeof hideFn === 'function') hideFn();
      this.moveCardToDifferentList(list.list_id, this.card.id);
    },
    // this method is here as AutoSizeInput is not firing @blur if area has .preventDefault() enabled.
    handleFocusOut() {
      this.editableItem = false;
    },
    getProjectTitle(projectId) {
      return this.getProjectById(projectId)?.title;
    },
    getProjectById(projectId) {
      const project = this.$store.getters.getTeamProjectById(projectId);
      if (!project) return;
      return project;
    },
    firstLevelClickHandler() {
      if (
        this.isViews ||
        this.isMyWork ||
        this.isAgents ||
        this.isAgent ||
        this.isManageSpaces ||
        this.viewEditMode ||
        (this.isEpicsBoard && !this.isEpic)
      )
        return;

      if (this.isCreateSpace) {
        this.goToManageSpaces();
        return;
      }

      if (this.isEpic) {
        this.goToRoadmap();
        return;
      }

      if (this.isNote) {
        this.minimizeNote();
        return;
      }

      this.$router.push(`/${this.teamSlug}/views`);
    },
    goToRoadmap() {
      if (
        this.$route.name === this.$constants.epicsBoardRoute.name &&
        this.$store.getters.getIsCardDisplayed(this.resourceId)
      ) {
        history.pushState(
          {
            ...history.state,
            cardId: null,
          },
          null,
          `/${this.teamSlug}/${this.$constants.epicsBoardRoute.path}`
        );
        this.$store.dispatch('removeDisplayedCardsByField', {
          field: 'id',
          value: this.resourceId,
        });
        return;
      }

      this.$router.push({ name: this.$constants.epicsBoardRoute.name });
    },
    minimizeNote() {
      //todo(senad): add event listener for this in new Note.vue component
      EventBus.$emit('note:minimize', this.resourceId);
    },
    goToManageSpaces() {
      this.$router.push(`/${this.teamSlug}/${this.$constants.manageSpacesRouteName}`);
    },
    goToSpace(e) {
      if (cmdClickOpenInNewTab(this.spaceLink, e)) return;

      e.preventDefault();
      if (this.currentProject.id !== this.resourceProject.id) {
        this.$store.dispatch('setCurrentProjectId', this.resourceProject.id);
      }

      const route = this.spaceLink;
      if (this.$store.getters.getIsCardDisplayed(this.resourceId)) {
        this.$store.dispatch('removeDisplayedCardsByField', {
          field: 'id',
          value: this.resourceId,
        });
      }
      if (this.$route.path === route) {
        history.pushState(null, null, route);
        return;
      }
      if (this.tabName === 'pages') {
        this.$store.dispatch('loadPages');
      }
      if (this.tabName === 'boards' || this.tabName === 'cards') {
        this.$store.dispatch('loadBoards');
      }
      this.$router.push(route);
    },

    boardItemClick(route) {
      if (this.$route.path !== route) {
        this.$store.dispatch('setCurrentBoardId', this.boardId);
        this.$store.dispatch('setCurrentProjectId', this.resourceProject?.id);
        this.$router.push(route);
      }
    },

    focusInputField() {
      this.editableItem = true;
      this.$nextTick(() => {
        this.$refs.headerNameInput.focus();
        this.$refs.headerNameInput.select();
      });
    },

    openPageOrBoard(e) {
      e.preventDefault();
      if (this.editableResourceName) {
        return;
      }

      if (cmdClickOpenInNewTab(this.resourceLink, e)) return;

      if (this.$route.path === this.resourceLink && !this.$store.getters.getIsAnyCardDisplayed) {
        this.generateLink(this.type);
      }

      if (this.isInbox && this.inboxPage) {
        this.$router.push(this.resourceLink);
      }

      if (this.type === ResourceType.Board || this.type === ResourceType.Sprint || this.typeCard) {
        this.$store.dispatch('setDisplayedCards');
        if (this.$route.path !== this.resourceLink) {
          this.boardItemClick(this.resourceLink);
        } else {
          history.pushState({ ...history.state, cardId: null }, null, this.resourceLink);
        }
      }
    },

    handleInputBlur() {
      this.hasFocus = false;
      if (this.type === 'newboard' && !this.inputValue) {
        // do nothing when there is no value
        return;
      }
      const oldTitle = this.itemTitle;
      this.editableItem = false;
      if (oldTitle === this.inputValue && this.type !== 'newboard') return;
      switch (this.type) {
        case 'newview':
        case 'view':
          if (this.inputValue.trim().length) {
            this.$store.dispatch('updateSelectedViewName', this.inputValue.trim());
            this.$emit('saveViewTitle', this.inputValue);
          } else this.inputValue = oldTitle;
          break;
        case 'page':
          this.translate('copyPageUrl');
          break;
        case 'board':
          this.updateBoardTitle(oldTitle);
          break;
        default:
          break;
      }
    },

    handleProjectTitleInputBlur() {
      this.hasFocusProjectTitle = false;
      const oldTitle = this.projectTitle;
      if (oldTitle === this.projectNameInput) return;
      if (this.projectNameInput === '') {
        this.projectNameInput = oldTitle;
        return;
      }
      this.updateProjectTitle();
    },

    updateProjectTitle() {
      this.$store.dispatch('updateProject', { projectData: { title: this.projectNameInput } });
    },

    updateBoardTitle(oldName) {
      if (!this.inputValue.trim()) {
        this.inputValue = oldName;
        return;
      }
      this.inputValue = this.inputValue.trim();
      const { lists: currentBoardLists } = this.currentBoard;
      const dashedBoardTitle = getDashedTitle(this.inputValue);
      const newTitle = this.inputValue;
      if (!currentBoardLists.length) {
        this.$store.dispatch('setDraftListInputVisibleOnOpen', true);
      }
      this.$store.dispatch('updateBoardPropertyInBoards', {
        boardId: this.currentBoard.id,
        propertyName: 'title',
        value: newTitle,
      });
      if (this.isFavourite(this.currentBoard.id, 'board')) {
        this.$store.dispatch('localUpdateResourceInFavourites', {
          resource_id: this.currentBoard.id,
          resource_type: 'board',
          title: newTitle,
        });
      }
      this.$store
        .dispatch('updateBoard', {
          id: this.currentBoard.id,
          board: { title: newTitle },
        })
        .then(({ data }) => {
          if (data.board.id === this.currentBoard.id) {
            const path = `/${this.$store.getters.getTeamSlug}/board-${this.currentBoard.id}-${dashedBoardTitle}`;
            if (this.$route.path !== path && !this.embed) {
              this.$router.replace({ path });
            }
          }
        })
        .catch(() => {
          this.$store.dispatch('updateBoardPropertyInBoards', {
            boardId: this.currentBoard.id,
            propertyName: 'title',
            value: oldName,
          });
          if (this.isFavourite(this.currentBoard.id, 'board')) {
            this.$store.dispatch('localUpdateResourceInFavourites', {
              resource_id: this.currentBoard.id,
              resource_type: 'board',
              title: oldName,
            });
          }
        });
    },

    openFilterMenu(event) {
      event.stopPropagation();
      event.preventDefault();
      this.$refs.headerNameInput.blur();
      if (this.isViewOpen) {
        EventBus.$emit('openFilterMenu');
      }
    },

    checkKey(event) {
      if (event.key === 'Tab') {
        event.preventDefault();
        event.stopPropagation();
        this.$refs.headerNameInput.blur();
      }
      if (event.key === 'Escape') {
        if (/* this.isNewPage ||  */ this.isNewBoard && this.inputValue === '') {
          // return and let global esc handle it
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        this.inputValue = this.itemTitle;
        this.$refs.headerNameInput.blur();
        return;
      }

      if (event.type === 'blur') {
        if (this.inputValue === '') {
          this.inputValue = this.itemTitle;
        }
        this.$refs.headerNameInput.blur();
      }
    },

    checkKeyProjectTitle(event) {
      const blurKeys = ['Enter', 'Escape', 'Tab'];
      if (blurKeys.includes(event.key)) {
        event.preventDefault();
        event.stopPropagation();
        this.$refs.headerNameProjectTitleInput.blur();
      }
    },

    listDropdownEnabled() {
      return !(this.card?.archived || this.cancelledOrCompletedSprint || this.isGuestReader);
    },

    handleInputFocus() {
      if (this.readOnly) return;
      this.hasFocus = true;
      this.editableItem = true;
    },

    handleProjectTitleInput() {
      this.hasFocusProjectTitle = true;
    },

    cardIdClickHandler() {
      if (this.card.archived) return;
      const cardRouteName = this.card.sprint_id ? ResourceType.SprintCard : ResourceType.Card;
      const routeCard = `/${this.teamSlug}/${cardRouteName}-${this.card.id}-${getDashedTitle(
        this.card.title
      )}`;
      this.$router.push({
        path: routeCard,
        query: {
          userNavigated: true,
          cardIdButton: true,
        },
      });
    },
  },
};
</script>

<style lang="scss">
.breadcrumbs-main {
  display: flex;
  align-items: center;
  height: 26px;
  min-width: 0;

  .breadcrumbs-wrap,
  .breadcrumb-page-parents {
    height: 26px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.1px;
    color: $stPrimaryColor;
    box-sizing: border-box;
    min-width: 0;
    @apply bg-background-strongest text-text-default;

    .breadcrumb-separator {
      margin: 0 4px;
    }

    .breadcrumb-item {
      display: flex;
      align-items: center;
      cursor: pointer;
      position: relative;
      white-space: nowrap;
      min-width: 0;
      max-width: fit-content;
      flex: 1 1 0;
      padding: 2px 4px;
      gap: 4px;

      &.disable-hover {
        @apply cursor-default pointer-events-none;
      }

      &:hover {
        @apply bg-hover-default;
        border-radius: 4px;

        .list-dropdown {
          color: #929292;
        }
        input {
          background: var(--hover) !important;
        }
      }

      .breadcrumb-item-text {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        padding: 1px 0px;
        line-height: 20px;
        @apply text-text-default;

        &.transparent-border {
          border: 1px transparent solid;
        }
        ::placeholder {
          @apply text-text-placeholder;
        }
        input {
          @apply bg-hover-default text-text-default;
          &:hover,
          &:focus {
            background: var(--hover) !important;
          }
        }
      }

      &.breadcrumb-list-active {
        @apply text-text-default;
      }

      &:last-child {
        flex-shrink: 0;
      }

      em {
        font-size: 16px;
        min-width: 16px;
        max-width: 16px;
        overflow: hidden;

        &.board-icon {
          color: #36a0f1;
        }

        &.page-icon {
          color: #77b255;
        }
      }

      &.editable-item {
        border: 1px solid transparent;
        border-radius: 4px;
        padding: 0;

        &:hover,
        &.edit-active {
          &:hover {
            @apply bg-hover-default;
          }
        }

        &.edit-active {
          cursor: text;
        }

        &.focused-input {
          @apply border
            border-solid
            border-blue-400;
          cursor: text;
          box-shadow: 0px 0px 0px 2px #e5f3ff;

          &.blank-field {
            .autosize-input {
              input {
                text-overflow: unset !important;
              }
            }
          }
          &.edit-active {
            @apply bg-hover-default;
          }
        }

        .autosize-input {
          input {
            border: none;
            outline: none;
            font-size: 14px;
            line-height: 16px;
            letter-spacing: 0.1px;
            @apply text-text-default bg-background-strongest;
          }
        }

        &.view-edit-mode {
          &.focused-input {
            @apply bg-background-strongest;

            &.edit-active {
              @apply bg-hover-default;
            }
          }

          input {
            @apply bg-background-strongest
              text-text-default
              hover:bg-important-hover;
          }
        }
      }

      &.card-breadcrumb {
        font-weight: 500;
        padding: 4px;
      }

      .page-parents-dropdown {
        position: absolute;
        display: flex;
        flex-direction: column;
        top: 25px;
        left: -12px;
        width: 240px;

        .page-parent-item {
          display: flex;
          align-items: center;
          padding: 10px 16px;
          box-sizing: border-box;
          min-height: 44px;

          .page-parent-title {
            font-size: 14px;
            line-height: 20px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            margin-left: 8px;
            @apply text-text-default;
          }

          &:hover {
            @apply bg-important-hover;
          }
        }
      }
    }

    .breadcrumb-item,
    .breadcrumb-separator {
      &.disabled-item {
        @apply bg-transparent
        select-none
        pointer-events-none
        text-inactive-text;

        span {
          @apply text-inactive-text;
        }

        em {
          @apply bg-red-600;
        }
      }
    }
  }

  .breadcrumb-buttons {
    display: flex;
    opacity: 1;

    .copy-link-button {
      height: 24px;
      width: 24px;
      display: flex;
      justify-content: center;
      align-items: center;
      border-radius: 4px;
      margin-left: 4px;

      &:hover {
        @apply bg-icon-bg;
        .custom-icons {
          &.copy-header-icon {
            @apply bg-icon-hover;
          }
        }
      }

      &.disabled-item {
        background: transparent;
        em {
          @apply bg-inactive-icon;
        }
      }
    }

    .tooltip {
      .tooltip-wrap {
        .tooltip-text {
          @apply font-normal;
        }
      }
    }
  }
}
</style>
